// ─── src/chat/AuraChat.jsx ────────────────────────────────────────────────────
// Single mount point for the entire three-state chat system.
// Renders exactly one state at a time: closed | open | fullchat
// Desktop: AuraMiniBar (open) / AuraWorkspace (fullchat)
// Mobile:  AuraBottomSheet handles both open + fullchat states
import { useEffect }         from "react";
import { useAura }           from "../stores/auraCore";
import { useFloatingUi }     from "../components/FloatingUiManager";
import AuraLauncher          from "./AuraLauncher";
import AuraMiniBar           from "./AuraMiniBar";
import AuraWorkspace         from "./AuraWorkspace";
import AuraBottomSheet       from "./AuraBottomSheet";

// ── Global keyframe animations (injected once here) ──────────────────────────
const KEYFRAMES = `
  @keyframes lwd-dot-pulse {
    0%, 80%, 100% { transform: scale(0.65); opacity: 0.45; }
    40%           { transform: scale(1.2);  opacity: 1;    }
  }
  @keyframes lwd-cursor-blink {
    50% { opacity: 0; }
  }
  @keyframes lwd-status-pulse {
    0%, 100% { opacity: 1;   transform: scale(1);    }
    50%      { opacity: 0.4; transform: scale(0.82); }
  }
  @keyframes shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

export default function AuraChat({ onNavigateHome }) {
  const chatUiState   = useAura((s) => s.chatUiState);
  const openMiniBar   = useAura((s) => s.openMiniBar);
  const openWorkspace = useAura((s) => s.openWorkspace);
  const sendMessage   = useAura((s) => s.sendMessage);
  const { isMobile }  = useFloatingUi();

  // Standard open → MiniBar
  useEffect(() => {
    const handler = (e) => {
      openMiniBar();
      const q = e.detail?.query;
      if (q) setTimeout(() => sendMessage(q), 300);
    };
    window.addEventListener("lwd:openAura", handler);
    return () => window.removeEventListener("lwd:openAura", handler);
  }, [openMiniBar, sendMessage]);

  // Full lights-out open → straight to AuraWorkspace
  useEffect(() => {
    const handler = (e) => {
      openWorkspace();
      const q = e.detail?.query;
      if (q) setTimeout(() => sendMessage(q), 300);
    };
    window.addEventListener("lwd:openAuraFull", handler);
    return () => window.removeEventListener("lwd:openAuraFull", handler);
  }, [openWorkspace, sendMessage]);

  return (
    <>
      <style>{KEYFRAMES}</style>
      {chatUiState === "closed"   && <AuraLauncher />}

      {/* Mobile: AuraBottomSheet handles both open and fullchat */}
      {isMobile && (chatUiState === "open" || chatUiState === "fullchat") && (
        <AuraBottomSheet />
      )}

      {/* Desktop: AuraMiniBar (open) / AuraWorkspace (fullchat) */}
      {!isMobile && chatUiState === "open"     && <AuraMiniBar />}
      {!isMobile && chatUiState === "fullchat" && (
        <AuraWorkspace
          onBack={onNavigateHome}
          onHome={onNavigateHome}
          onVenues={onNavigateHome}
        />
      )}
    </>
  );
}
